/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import { useTheme } from 'foundations-theming/utils';
import FilterOperatorDisplay from 'customer-data-filters/components/operator/FilterOperatorDisplay';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useMemo } from 'react';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { getReferenceType } from '../../../../metadata/references/external/external-mapping';
import { htmlNormalizedToTextFilter, isHtmlFieldType } from '../../filter-utils';
import { getTableLabelWithSelfJoins } from '../../../../utils/table-utils';
import { toDSFilterOperator, getLabelString, buildSpecialOptionsByReferenceType, toDSFieldRecord, getPropertyFieldDefinitions, getObjectTypeFamily, isOptionTypeSupportedForReferenceResolverLite } from '../../xo-filter-utils';
import { getDangerousRollupPropertyTableLabel, hasObjectTypeId, isDangerousRollupProperty, shouldHideProperty } from '../../../../utils/source-utils';
import ConnectReferenceResolvers from 'reference-resolvers/ConnectReferenceResolvers';
import { useProperty, useTableDescription, useTableSource, useTableSources } from '../SourceMeta';
import { getPropertyResolver } from 'reference-resolvers-lite/utils/getPropertyResolver';
import { useSnowflakeJoinMetadata } from '../../../../metadata/joins-with-meta';
import { getIsUngated, useFilterRelevantUserInfo } from '../../../../../shared/lib/utils';
import { Link } from 'foundations-components/transitional/components/Link';
// TODO: remove this when we add more structured metadata validation - https://git.hubteam.com/HubSpot/report-creation-issues/issues/867
// @returns {{ error?: node, warning?: node }}
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const validateProperty = (property, tableDescription, tables) => {
  if (shouldHideProperty(property)) {
    return {
      error: I18n.text('reporting-snowflake.metadata-errors.invalid-property')
    };
  }
  if (isDangerousRollupProperty(tableDescription, property)) {
    return {
      warning: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "reporting-snowflake.metadata-warnings.dangerous-rollup-property_jsx",
        elements: {
          Link
        },
        options: {
          external: true,
          tableName: getDangerousRollupPropertyTableLabel(tableDescription, tables, property),
          href: 'https://knowledge.hubspot.com/reports/understand-the-custom-report-builder#rollup-fields'
        }
      })
    };
  }
  return {};
};
const getMappedProps = (filter, source, property, resolvers, userId, userGates) => {
  let {
    filter: propertyFilter
  } = filter;
  if (isHtmlFieldType(property)) {
    propertyFilter = htmlNormalizedToTextFilter(filter).filter;
  }
  const operator = toDSFilterOperator(propertyFilter, property, source, userGates);
  const field = toDSFieldRecord(property, source, userGates);
  const getReferencedObjectType = () => getReferenceType(source, property);
  const getFamilyValueResolver = () => {
    const useReferenceResolverLite = isOptionTypeSupportedForReferenceResolverLite(source, property, userGates);
    const referenceResolverLite = useReferenceResolverLite ? getPropertyResolver({
      property: field,
      objectTypeId: source.objectTypeId
    }) : null;
    return referenceResolverLite || resolvers[getReferencedObjectType()] || null;
  };
  return {
    currencyCode: 'USD',
    isReadOnly: true,
    isXoEnabled: true,
    operator,
    getSpecialOptionsForReferenceType: buildSpecialOptionsByReferenceType(property.type, userId),
    getLabelString: () => getLabelString(field),
    getReferencedObjectType,
    getFamilyValueResolver,
    filterFamily: hasObjectTypeId(source) ? getObjectTypeFamily(source.objectTypeId) : source.dataSourceId,
    fieldDefinitions: getPropertyFieldDefinitions()
  };
};
const PropertyFilterLabel = ({
  filter,
  resolvers
}) => {
  const trellisTheme = useTheme();
  const {
    field
  } = filter;
  const {
    table: tableName,
    name: fieldName
  } = field;
  const {
    userId,
    userGates
  } = useFilterRelevantUserInfo();
  const tables = useTableSources();
  const source = useTableSource(tableName);
  const property = useProperty(tableName, fieldName);
  const tableDescription = useTableDescription();
  const {
    data: allJoinMetadata
  } = useSnowflakeJoinMetadata();
  if (property == null) {
    throw new Error(`PropertyFilterLabel can only be rendered when there is a matching existing property in report meta! field: ${field}`);
  }
  if (tableDescription == null) {
    throw new Error(`PropertyFilterLabel can only be rendered when there is a table description in the report! field: ${field}`);
  }
  const tableLabel = getTableLabelWithSelfJoins(tableName, tableDescription, source, allJoinMetadata);
  const filterOperatorDisplayProps = useMemo(() => {
    if (userId === null || userGates.length === 0) {
      return null;
    }
    return getMappedProps(filter, source, property, resolvers, userId, userGates);
  }, [filter, source, property, resolvers, userId, userGates]);
  const {
    error,
    warning
  } = validateProperty(property, tableDescription, tables);
  const style = error ? {
    color: trellisTheme.color['text-alert-default']
  } : warning ? {
    color: trellisTheme.color['fill-caution-pressed']
  } : {};
  const tooltipMessage = error || warning;
  if (!filterOperatorDisplayProps) {
    return null;
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "property-filter-label",
    children: [/*#__PURE__*/_jsx("span", {
      style: {
        display: 'block',
        fontWeight: 600,
        marginBottom: -4
      },
      children: /*#__PURE__*/_jsx(TruncateString, {
        children: tableLabel
      })
    }), /*#__PURE__*/_jsx(Tooltip, {
      title: tooltipMessage,
      disabled: !tooltipMessage,
      children: /*#__PURE__*/_jsx("span", {
        style: style,
        children: filterOperatorDisplayProps.operator ? /*#__PURE__*/_jsx(FilterOperatorDisplay, Object.assign({}, filterOperatorDisplayProps, {
          getIsUngated: gate => getIsUngated(gate, userGates)
        })) : /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx("strong", {
            children: filterOperatorDisplayProps.getLabelString()
          })
        })
      })
    })]
  });
};
export default ConnectReferenceResolvers(resolvers => ({
  resolvers
}))(PropertyFilterLabel);